import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { businessService } from '@/services/business.service';
import { HelpCircle, Book, MessageCircle, Phone, Mail, Search, ChevronRight, FileText, Users, Settings, CreditCard, Shield, Zap } from 'lucide-react';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import { LEARNER_CONTACT_SUPPORT_PERMISSION } from '@/constants/constant-permissions';

export default function HelpCenter() {
  usePermissionRouteAccess([LEARNER_CONTACT_SUPPORT_PERMISSION.LEARNER_CONTACT_SUPPORT_READ]);

  const [searchQuery, setSearchQuery] = useState('');

  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const contactEmail = businessData?.data?.contact_email?.trim() || businessData?.contact_email?.trim() || 'support@skillzen.ai';
  const contactPhone = businessData?.data?.contact_phone_number?.trim() || businessData?.contact_phone_number?.trim() || 'Mon-Fri, 9AM-6PM EST';

  const helpCategories = [
    {
      icon: <Book className="w-6 h-6" />,
      title: 'Getting Started',
      description: 'Learn the basics of using the platform',
      articles: 12,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Courses & Learning',
      description: 'Everything about courses, modules, and assessments',
      articles: 24,
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Study Groups',
      description: 'Collaborate with other learners',
      articles: 8,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Account & Settings',
      description: 'Manage your profile and preferences',
      articles: 15,
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: 'Billing & Subscriptions',
      description: 'Payment and subscription information',
      articles: 10,
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Privacy & Security',
      description: 'Keep your account safe and secure',
      articles: 7,
      color: 'bg-red-100 text-red-600'
    }
  ];

  const popularArticles = [
    { id: 1, title: 'How to enroll in a course', category: 'Getting Started', views: 1234 },
    { id: 2, title: 'Understanding learning paths', category: 'Courses & Learning', views: 987 },
    { id: 3, title: 'Joining a study group', category: 'Study Groups', views: 765 },
    { id: 4, title: 'Resetting your password', category: 'Account & Settings', views: 654 },
    { id: 5, title: 'Certificate verification process', category: 'Courses & Learning', views: 543 }
  ];

  const faqs = [
    {
      question: 'How do I track my learning progress?',
      answer: 'You can track your progress from the Progress page in your learner dashboard. It shows completed modules, time spent, and achievements.'
    },
    {
      question: 'Can I download course materials for offline viewing?',
      answer: 'Yes, you can download course materials from the Downloads section. PDFs and videos are available for offline access.'
    },
    {
      question: 'How do I get my course completion certificate?',
      answer: 'Certificates are automatically generated upon course completion. You can view and download them from the Certificates page.'
    },
    {
      question: 'What should I do if I encounter technical issues?',
      answer: 'For technical issues, try clearing your browser cache first. If the problem persists, contact our support team.'
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">How can we help you?</h1>
        <p className="text-gray-500">Search our knowledge base or browse categories below</p>
        
        {/* Search Bar */}
        <div className="mt-6 max-w-2xl mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help articles, tutorials, or FAQs..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
          <MessageCircle className="w-8 h-8 text-primary-600" />
          <div className="text-left">
            <h3 className="font-medium text-gray-900">Live Chat</h3>
            <p className="text-sm text-gray-500">Chat with our support team</p>
          </div>
        </button>
        <button
          onClick={() => window.open(`mailto:${contactEmail}`)}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        >
          <Mail className="w-8 h-8 text-primary-600" />
          <div className="text-left">
            <h3 className="font-medium text-gray-900">Email Support</h3>
            <p className="text-sm text-gray-500">{contactEmail}</p>
          </div>
        </button>
        <button
          onClick={() => /\d/.test(contactPhone) ? window.open(`tel:${contactPhone}`) : undefined}
          className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        >
          <Phone className="w-8 h-8 text-primary-600" />
          <div className="text-left">
            <h3 className="font-medium text-gray-900">Call Us</h3>
            <p className="text-sm text-gray-500">{contactPhone}</p>
          </div>
        </button>
      </div>

      {/* Help Categories */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Browse by Category</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {helpCategories.map((category, index) => (
            <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${category.color}`}>
                  {category.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{category.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                  <p className="text-xs text-gray-400 mt-2">{category.articles} articles</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Popular Articles */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Popular Articles</h2>
        <div className="bg-white rounded-lg border border-gray-200">
          {popularArticles.map((article, index) => (
            <div 
              key={article.id}
              className={`p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${
                index !== popularArticles.length - 1 ? 'border-b border-gray-200' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-yellow-500" />
                <div>
                  <h3 className="font-medium text-gray-900">{article.title}</h3>
                  <p className="text-sm text-gray-500">{article.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>{article.views} views</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQs */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details key={index} className="bg-white rounded-lg border border-gray-200 p-4">
              <summary className="font-medium text-gray-900 cursor-pointer list-none flex items-center justify-between">
                {faq.question}
                <ChevronRight className="w-5 h-5 text-gray-400 transform transition-transform" />
              </summary>
              <p className="text-gray-600 mt-3">{faq.answer}</p>
            </details>
          ))}
        </div>
      </div>

      {/* Still Need Help */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-6 text-white text-center">
        <HelpCircle className="w-12 h-12 mx-auto mb-3" />
        <h3 className="text-xl font-semibold mb-2">Still need help?</h3>
        <p className="mb-4">Our support team is here to assist you</p>
        <button className="px-6 py-2 bg-white text-primary-600 rounded-lg hover:bg-gray-100 transition-colors font-medium">
          Contact Support
        </button>
      </div>
    </div>
  );
}